import ApiClient from '@/api/ApiClient';

import type {
  ChangeAssigneeRequest,
  CompleteTaskResponse,
  LocalDate,
  PagedResponseTaskResponse,
  TaskAction,
  TaskCompletionRequest,
  TaskResponse,
} from '../models';

export type GetTasksByRequest = {
  filterByCreatedDate?: LocalDate;
  filterByDueDate?: LocalDate;
  filterByStartedBy?: string;
  filterByTaskName?: string;
};

export type GetAllTasksRequest = {
  filterByAssignee?: string;
  filterByCreatedDate?: LocalDate;
  filterByDueDate?: LocalDate;
  filterByStartedBy?: string;
  filterByTaskName?: string;
  firstResult?: number;
  maxSize?: number;
  orderByCreatedDate?: string;
  orderByDueDate?: string;
};

export class TaskClient {
  apiClient: ApiClient;

  constructor(apiClient?: ApiClient) {
    this.apiClient = apiClient || ApiClient.instance;
  }

  getActionConfigs(): Promise<Record<string, Record<string, unknown>>> {
    return this.apiClient.get({
      path: '/api/mass-task-action/config',
    });
  }

  storeColumnsConfig(request: { body?: Record<string, unknown>; } = {}): Promise<void> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/mass-task-action/config',
      body,
    });
  }

  completeTasks(request: { body?: TaskCompletionRequest; } = {}): Promise<CompleteTaskResponse[]> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/mass-task-action/tasks',
      body,
    });
  }

  getMassTasks(request: { processKey: string; taskKey: string; }): Promise<TaskResponse[]> {
    const { processKey, taskKey } = request;
    return this.apiClient.get({
      path: `/api/mass-task-action/tasks/${processKey}/${taskKey}`,
    });
  }

  getTasksBy(request: GetTasksByRequest = {}): Promise<PagedResponseTaskResponse> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/tasks',
      params,
    });
  }

  multiTaskAction(request: { body?: TaskAction[]; } = {}): Promise<void> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/tasks',
      body,
    });
  }

  getAllTasks(request: GetAllTasksRequest = {}): Promise<PagedResponseTaskResponse> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/tasks/all',
      params,
    });
  }

  getTaskDefaultVariables(request: { body?: Record<string, string>; } = {}): Promise<Record<string, unknown>> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/tasks/defaultvariables',
      body,
    });
  }

  getUserNextTaskInProcess(request: { procInstId: string; }): Promise<TaskResponse> {
    const { procInstId } = request;
    return this.apiClient.get({
      path: `/api/tasks/next/${procInstId}`,
    });
  }

  getActiveTasks(request: { businesskey: string; }): Promise<void> {
    const { businesskey } = request;
    return this.apiClient.get({
      path: `/api/tasks/${businesskey}/active-tasks`,
    });
  }

  completeTaskById(request: { id: string; }): Promise<void> {
    const { id } = request;
    return this.apiClient.post({
      path: `/api/tasks/${id}`,
    });
  }

  delegateTask(request: { id: string; userId: string; }): Promise<void> {
    const { id, userId } = request;
    return this.apiClient.post({
      path: `/api/tasks/${id}/delegate/${userId}`,
    });
  }

  changeAssignees(request: { id: string; body?: ChangeAssigneeRequest; }): Promise<void> {
    const { id, body } = request;
    return this.apiClient.post({
      path: `/api/tasks/${id}/identitylinks`,
      body,
    });
  }

  getTaskVariables(request: { id: string; }): Promise<Record<string, unknown>> {
    const { id } = request;
    return this.apiClient.get({
      path: `/api/tasks/${id}/variables`,
    });
  }
}

export default TaskClient;
